import React, { useEffect, useState } from "react";
import { Button, Drawer, Image, Input, Space, Tag, message } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { ticketAPIWithErrorHandling } from "../../../api/ticket/ticket";
import { statusTag, ticketCode, currentValueOf, staffRequesterOf } from "./ticketMeta";
import { neutral, spacing } from "../../../theme/tokens";

/**
 * Màn duyệt một yêu cầu hỗ trợ.
 *
 * Hai cột "Hiện tại | Đề nghị" là lý do tồn tại của màn này: dữ liệu có cấu
 * trúc nên cán bộ đối chiếu trong vài giây thay vì đọc một đoạn văn rồi tự bóc
 * giá trị ra.
 *
 * Hệ thống KHÔNG tự ghi vào hồ sơ khi bấm Hoàn thành — cán bộ vẫn sửa tay sau
 * khi xác minh giấy tờ, nên nút Hoàn thành bắt buộc ghi rõ đã sửa gì và có sẵn
 * link mở thẳng hồ sơ cư dân ngay cạnh.
 */
const CompareBox = ({ label, value, tone }) => (
  <div
    className={`flex-1 rounded border p-3 ${
      tone === "new" ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-gray-50"
    }`}
  >
    <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-sm ${tone === "new" ? "font-semibold text-blue-800" : "text-gray-700"}`}>
      {value || <span className="italic text-gray-400">Không có dữ liệu</span>}
    </div>
  </div>
);

export default function TicketReviewDrawer({ open, ticket, subtypeLabel, onClose, onDone }) {
  const [response, setResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setResponse(ticket?.response || "");
  }, [ticket]);

  if (!ticket) return null;

  const submit = async (status) => {
    const text = response.trim();

    if (status === "rejected" && !text) {
      message.warning("Vui lòng nhập lý do từ chối để cư dân biết vì sao.");
      return;
    }

    // Không tự ghi vào hồ sơ nên "Hoàn thành" chỉ là lời hứa nếu không ghi lại
    // đã sửa gì. Bắt buộc nhập để còn đối chiếu với nhật ký của hồ sơ gốc.
    if (status === "completed" && !text) {
      message.warning("Vui lòng ghi rõ đã cập nhật những gì trong hồ sơ.");
      return;
    }

    setSubmitting(true);
    try {
      await ticketAPIWithErrorHandling.replyTicket(ticket.id, { status, response: text });
      message.success("Đã cập nhật yêu cầu hỗ trợ");
      onDone();
    } catch (error) {
      message.error(error.message || "Không cập nhật được yêu cầu");
    } finally {
      setSubmitting(false);
    }
  };

  const closed = ["completed", "rejected", "cancelled"].includes(ticket.status);
  // Vé trưởng khu phố gửi thay cư dân: người đề nghị không phải chủ hồ sơ.
  const staff = staffRequesterOf(ticket);
  const requesterLabel = staff ? "Trưởng khu phố" : "Cư dân";

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <span className="font-mono">{ticketCode(ticket.id)}</span>
          <span>{subtypeLabel(ticket.subtype)}</span>
          {statusTag(ticket.status)}
        </div>
      }
      placement="right"
      width="min(620px, 100vw)"
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { background: neutral[50], padding: spacing[6] } }}
      footer={
        closed ? (
          <div className="text-sm text-gray-500">Yêu cầu đã kết thúc.</div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", gap: spacing[2] }}>
            <Button onClick={() => submit("processing")} disabled={submitting}>
              Đang xác minh
            </Button>
            <Space>
              <Button danger loading={submitting} onClick={() => submit("rejected")}>
                Từ chối
              </Button>
              <Button type="primary" loading={submitting} onClick={() => submit("completed")}>
                Hoàn thành
              </Button>
            </Space>
          </div>
        )
      }
    >
      <div className="mb-4 rounded border border-gray-200 bg-white p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-gray-800">{ticket.customer?.name || "—"}</div>
            <div className="mt-1 text-xs text-gray-500">
              {ticket.customer?.code} · {ticket.customer?.phone || (staff ? "—" : ticket.phone)}
            </div>
            {ticket.organization?.name && (
              <Tag color="default" className="mt-2">{ticket.organization.name}</Tag>
            )}
          </div>
          {ticket.customer_id && (
            <Link to={`/customers/${ticket.customer_id}`} target="_blank">
              <Button size="small" icon={<ExportOutlined />}>Mở hồ sơ</Button>
            </Link>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-400">
          Gửi lúc {dayjs(ticket.created_at).format("HH:mm DD/MM/YYYY")}
        </div>
        {staff && (
          <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-600">
            Đề nghị bởi trưởng khu phố{" "}
            <span className="font-semibold text-gray-800">{staff.name}</span>
            {staff.phone && <> · {staff.phone}</>}
          </div>
        )}
      </div>

      <div className="mb-4 flex gap-3">
        <CompareBox label="Hiện tại" value={currentValueOf(ticket)} />
        <CompareBox label={`${requesterLabel} đề nghị`} value={ticket.requested_value} tone="new" />
      </div>

      {ticket.content && (
        <div className="mb-4 rounded border border-gray-200 bg-white p-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">Ghi chú của {requesterLabel.toLowerCase()}</div>
          <div className="text-sm italic text-gray-700">{ticket.content}</div>
        </div>
      )}

      {ticket.images?.length > 0 && (
        <div className="mb-4 rounded border border-gray-200 bg-white p-3">
          <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Giấy tờ đính kèm</div>
          <Image.PreviewGroup>
            <Space size={8} wrap>
              {ticket.images.map((img, idx) => (
                <Image
                  key={img.id ?? idx}
                  width={88}
                  height={88}
                  src={img.url}
                  className="rounded border border-gray-200 object-cover"
                />
              ))}
            </Space>
          </Image.PreviewGroup>
        </div>
      )}

      <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        Hệ thống không tự sửa hồ sơ. Hãy cập nhật hồ sơ cư dân trước, rồi ghi lại bên dưới đã
        sửa những gì.
      </div>

      <div className="mt-3">
        <div className="mb-1 text-sm font-medium text-gray-700">
          Nội dung trả lời {requesterLabel.toLowerCase()}
        </div>
        <Input.TextArea
          rows={4}
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          disabled={closed}
          placeholder="Ví dụ: Đã cập nhật họ tên trong hồ sơ theo CCCD đính kèm."
        />
      </div>
    </Drawer>
  );
}
